import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { useStore } from "@nanostores/react";
import {
  $organizationId,
  organisationActions,
} from "@vega-api/custom/stores/organisations.store";
import { type ManageableLimitedPartnersResponse } from "@vega-api/generated/types/manageableLimitedPartnersResponse";
import { useGetManageableLimitedPartners } from "@vega-api/generated/user-management-v1/user-management-v1";
import { isFalsy } from "@vega-core/utils/isFalsy";

import { CommonPaths } from "../paths";

export const useOrganization = () => {
  const selectedOrgId = useStore($organizationId);
  const setOrgs = organisationActions.setOrganisationList;
  const setSelectedOrg = organisationActions.setOrganisation;
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: orgs,
    isLoading: isLoadingOrgs,
    isError,
    error,
  } = useGetManageableLimitedPartners({
    query: { select: (data) => data.data },
  });

  const isPublicRoute = [
    CommonPaths.Login,
    CommonPaths.NotFound,
    CommonPaths.OktaAuth,
    CommonPaths.Welcome,
  ].includes(location.pathname as CommonPaths & string);

  const [isReady, setIsReady] = useState(false);

  const handleInitialOrgSelection = useCallback(() => {
    const storedOrgId = localStorage.getItem("$organizationId");
    const storedOrgList = localStorage.getItem("$organizationList");
    const parsedOrgList: ManageableLimitedPartnersResponse[] = JSON.parse(
      storedOrgList || "[]",
    );

    if (storedOrgId && parsedOrgList.length > 0) {
      const org = parsedOrgList.find((o) => o.id === storedOrgId);

      setOrgs(parsedOrgList);
      org && setSelectedOrg(org);
    }
  }, [setOrgs, setSelectedOrg]);

  const handleMultipleOrganisations = useCallback(
    (orgs: ManageableLimitedPartnersResponse[]) => {
      const storedOrgId = localStorage.getItem("$organizationId");
      const storedOrgList = localStorage.getItem("$organizationList");
      const parsedOrgList: ManageableLimitedPartnersResponse[] = JSON.parse(
        storedOrgList || "[]",
      );

      if (storedOrgId && parsedOrgList.length > 0) return;
      setOrgs(orgs);

      if (isFalsy(selectedOrgId)) {
        navigate(CommonPaths.SelectOrg);
      }
    },
    [navigate, selectedOrgId, setOrgs],
  );

  const handleSingleOrganisation = useCallback(
    (orgs: ManageableLimitedPartnersResponse[]) => {
      setOrgs(orgs);
      setSelectedOrg(orgs[0]);
    },
    [setOrgs, setSelectedOrg],
  );

  const handleErrorFetchingOrgs = useCallback(() => {
    console.error("Error fetching organizations:", error);
    // Implement user feedback or retry logic here
  }, [error]);

  const handleOrganisationCheck = useCallback(async () => {
    if (selectedOrgId && location.pathname === CommonPaths.SelectOrg) {
      navigate(CommonPaths.Home);
      setIsReady(true);

      return;
    }

    if (isLoadingOrgs || !orgs) {
      // Early return if still loading or no orgs data yet
      return;
    }

    if (isError) {
      handleErrorFetchingOrgs();
      setIsReady(true);

      return;
    }

    if (!selectedOrgId) {
      handleInitialOrgSelection();
    }

    if (orgs.length > 0) {
      if (orgs.length === 1) {
        handleSingleOrganisation(orgs);
      } else {
        handleMultipleOrganisations(orgs);
      }
    }
    setIsReady(true);
  }, [
    selectedOrgId,
    location.pathname,
    navigate,
    isLoadingOrgs,
    isError,
    handleErrorFetchingOrgs,
    handleInitialOrgSelection,
    orgs,
    handleSingleOrganisation,
    handleMultipleOrganisations,
  ]);

  useEffect(() => {
    if (isPublicRoute) return;
    handleOrganisationCheck();
  }, [handleOrganisationCheck, isPublicRoute]);

  return {
    isLoading: isLoadingOrgs,
    hasError: isError,
    error,
    isReady,
    selectedOrgId,
  };
};
